import { TemperatureThresholds, WbgtThresholds } from '../interfaces/organization.interface';

/**
 * MQTTサービスが書き込む statusNo から、Angular UI で使用する内部ステータスキーへのマッピング。
 *
 * WBGT SENSOR_STATUS (apps/mqtt/src/constants/app-const.ts):
 *   no: "1"  → ほぼ安全   → normal    → bg: #305496
 *   no: "2"  → ほぼ安全   → normal    → bg: #548235
 *   no: "3"  → 注意       → caution   → bg: #ffff00
 *   no: "10" → 警戒       → alert     → bg: #ff9900
 *   no: "11" → 厳重警戒   → danger    → bg: #ff0000
 *   no: "20" → 危険       → danger    → bg: #a50021
 *   no: "99" → 停止       → stopped   → bg: #757575
 *
 * WIND_SENSOR_STATUS:
 *   no: "1"  → ほぼ安全   → normal    → bg: #003366
 *   no: "2"  → ほぼ安全   → normal    → bg: #339933
 *   no: "3"  → 注意       → caution   → bg: #CCFF33
 *   no: "10" → 注意       → caution   → bg: #FFFF00
 *   no: "20" → 警報       → danger    → bg: #FF0000
 *   no: "99" → 停止       → stopped   → bg: #757575
 *
 * NOISE_SENSOR_STATUS:
 *   no: "1"  → 基準値未満 → normal    → bg: #003366
 *   no: "2"  → 基準値未満 → normal    → bg: #339933
 *   no: "10" → 騒音注意   → caution   → bg: #FFFF00
 *   no: "11" → 振動注意   → caution   → bg: #CCFF33
 *   no: "20" → 騒音警報   → danger    → bg: #FF0000
 *   no: "21" → 振動警報   → danger    → bg: #FF6600
 *   no: "99" → 停止       → stopped   → bg: #757575
 *
 * TEMPERATURE_SENSOR_STATUS:
 *   no: "1"  → 35℃以上   → bg: #B40068
 *   no: "2"  → 30℃以上   → bg: #FF2800
 *   no: "3"  → 25℃以上   → bg: #FF9900
 *   no: "4"  → 20℃以上   → bg: #FAF500
 *   no: "5"  → 15℃以上   → bg: #FFFF96
 *   no: "6"  → 10℃以上   → bg: #FFFFF0
 *   no: "7"  → 5℃以上    → bg: #B9EBFF
 *   no: "8"  → 要注意(5℃以下)  → bg: #0096FF
 *   no: "9"  → 要注意(0℃以下)  → bg: #0041FF
 *   no: "10" → 要注意(-5℃以下) → bg: #002080
 *   no: "99" → 停止       → bg: #757575
 */

export type SensorStatusKey = 'normal' | 'caution' | 'alert' | 'danger' | 'stopped';

export interface StatusLegendItem {
  statusNo: string;
  label: string;
  range: string;
  color: string;
  group?: string;
}

export const DEFAULT_WBGT_THRESHOLDS: WbgtThresholds = {
  almostSafe: 25,
  caution: 26,
  warning: 28,
  severeWarning: 30,
  danger: 33,
};

export const DEFAULT_TEMPERATURE_THRESHOLDS: TemperatureThresholds = {
  freezingDanger: -5,
  freezingCaution: 0,
  coldCaution: 5,
  mild: 5,
  tenOrMore: 10,
  fifteenOrMore: 15,
  twentyOrMore: 20,
  twentyFiveOrMore: 25,
  thirtyOrMore: 30,
  thirtyFiveOrMore: 35,
};

type PartialWbgtThresholdInput = Partial<Record<keyof WbgtThresholds, number | string | null | undefined>>;
type PartialTemperatureThresholdInput = Partial<Record<keyof TemperatureThresholds, number | string | null | undefined>>;

const STATUS_NO_MAP: Record<string, SensorStatusKey> = {
  '1':  'normal',
  '2':  'normal',
  '3':  'caution',
  '10': 'alert',
  '11': 'danger',
  '20': 'danger',
  '99': 'stopped',
};

/**
 * MQTTが書き込む statusNo を Angular UI の内部ステータスキーに変換する。
 * 未知の statusNo の場合は undefined を返す。
 */
export function mapSensorStatusNo(statusNo: string | undefined): SensorStatusKey | undefined {
  if (statusNo === undefined) return undefined;
  return STATUS_NO_MAP[statusNo];
}

export function mapNoiseStatusNo(statusNo: string | undefined): SensorStatusKey | undefined {
  switch (statusNo) {
    case '1':
    case '2':
      return 'normal';
    case '10':
    case '11':
      return 'caution';
    case '20':
    case '21':
      return 'danger';
    case '99':
      return 'stopped';
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// センサー鮮度チェック — 最終更新から一定時間経過で「停止」扱い
// ---------------------------------------------------------------------------

/** 1時間（ミリ秒） */
const STALE_THRESHOLD_MS = 60 * 60 * 1000;

/**
 * MQTT が書き込む最終更新日時（"YYYY-MM-DD HH:mm:ss" 形式、JST）が
 * 現在時刻から閾値(デフォルト1時間)以上経過しているかを判定する。
 * 日時が未指定・パース不能の場合も true（停止扱い）を返す。
 */
export function isSensorStale(dateStr: string | undefined, thresholdMs = STALE_THRESHOLD_MS): boolean {
  if (!dateStr) return true;
  // "YYYY-MM-DD HH:mm:ss" (JST) → ISO8601
  const isoStr = dateStr.replace(' ', 'T') + '+09:00';
  const sensorTime = new Date(isoStr);
  if (isNaN(sensorTime.getTime())) return true;
  return (Date.now() - sensorTime.getTime()) > thresholdMs;
}

// ---------------------------------------------------------------------------
// 危険度順序 — statusNo の深刻度を数値化（値が大きいほど危険）
// "99"（停止）はセンサーオフラインであり、危険度としては最低優先度とする。
// ---------------------------------------------------------------------------

/** WBGT（夏季）の危険度マップ */
const WBGT_SEVERITY: Record<string, number> = {
  '99': 0,
  '1':  1,
  '2':  2,
  '3':  3,
  '10': 4,
  '11': 5,
  '20': 6,
};

/**
 * 気温（冬季）の危険度マップ
 * statusNo が大きいほど低温 → 危険。ただし no:1（35℃以上）も高温で危険。
 * 冬季の主要リスクは低温なので、低温側 (8,9,10) を最も深刻度が高くする。
 * 高温側 (1,2,3) も危険だが冬季では低温リスクが主なので中間レベル。
 */
const TEMP_SEVERITY: Record<string, number> = {
  '99': 0,
  '6':  1,  // 10℃以上 — 安全
  '5':  2,  // 15℃以上
  '4':  3,  // 20℃以上
  '3':  4,  // 25℃以上
  '2':  5,  // 30℃以上
  '1':  6,  // 35℃以上
  '7':  7,  // 5℃以上 — 要注意ゾーン開始
  '8':  8,  // 5℃以下
  '9':  9,  // 0℃以下
  '10': 10, // -5℃以下
};

/** 後方互換用エイリアス */
const STATUS_SEVERITY = WBGT_SEVERITY;

export const NOISE_SEVERITY: Record<string, number> = {
  '99': 0,
  '1':  1,
  '2':  2,
  '10': 3,
  '11': 4,
  '20': 5,
  '21': 6,
};

/**
 * 複数のセンサーデータから最もステータスが悪い（危険度が高い）ものを選択する。
 * 停止（statusNo: '99'）センサーはワースト判定から除外し、
 * 稼働中のセンサーのみで最悪ステータスを決定する。
 * 全て停止の場合のみ停止のセンサーを返す。
 */
function findWorstSensor<T extends { statusNo?: string }>(
  sensors: T[],
  severityMap: Record<string, number>,
): T | undefined {
  if (!sensors || sensors.length === 0) return undefined;

  // 停止センサーを除外
  const activeSensors = sensors.filter(s => s.statusNo !== '99');

  // 全て停止の場合は最初の停止センサーを返す
  if (activeSensors.length === 0) return sensors[0];
  if (activeSensors.length === 1) return activeSensors[0];

  return activeSensors.reduce((worst, current) => {
    const worstSeverity = severityMap[worst.statusNo || ''] ?? -1;
    const currentSeverity = severityMap[current.statusNo || ''] ?? -1;
    return currentSeverity > worstSeverity ? current : worst;
  });
}

export function getWorstSensor<T extends { statusNo?: string }>(sensors: T[]): T | undefined {
  return findWorstSensor(sensors, STATUS_SEVERITY);
}

export function getWorstNoiseSensor<T extends { statusNo?: string }>(sensors: T[]): T | undefined {
  return findWorstSensor(sensors, NOISE_SEVERITY);
}

// ---------------------------------------------------------------------------
// ステータスバッジ CSS クラス（オリジナル配色に準拠）
// CSS クラスは styles.scss にグローバル定義
// ---------------------------------------------------------------------------

/**
 * WBGT ステータスバッジの CSS クラス名を返す。
 * statusNo に基づいてオリジナルの配色を適用する。
 */
export function getWbgtStatusBadgeClass(statusNo: string | undefined): string {
  switch (statusNo) {
    case '1':  return 'status-badge status-badge--wbgt-1';
    case '2':  return 'status-badge status-badge--wbgt-2';
    case '3':  return 'status-badge status-badge--wbgt-3';
    case '10': return 'status-badge status-badge--wbgt-10';
    case '11': return 'status-badge status-badge--wbgt-11';
    case '20': return 'status-badge status-badge--wbgt-20';
    case '99': return 'status-badge status-badge--stopped';
    default:   return 'status-badge status-badge--unknown';
  }
}

/**
 * WIND ステータスバッジの CSS クラス名を返す。
 * statusNo に基づいてオリジナルの配色を適用する。
 */
export function getWindStatusBadgeClass(statusNo: string | undefined): string {
  switch (statusNo) {
    case '1':  return 'status-badge status-badge--wind-1';
    case '2':  return 'status-badge status-badge--wind-2';
    case '3':  return 'status-badge status-badge--wind-3';
    case '10': return 'status-badge status-badge--wind-10';
    case '20': return 'status-badge status-badge--wind-20';
    case '99': return 'status-badge status-badge--stopped';
    default:   return 'status-badge status-badge--unknown';
  }
}

/**
 * NOISE ステータスバッジの CSS クラス名を返す。
 * statusNo に基づいてオリジナルの配色を適用する。
 */
export function getNoiseStatusBadgeClass(statusNo: string | undefined): string {
  switch (statusNo) {
    case '1':  return 'status-badge status-badge--noise-1';
    case '2':  return 'status-badge status-badge--noise-2';
    case '10': return 'status-badge status-badge--noise-10';
    case '11': return 'status-badge status-badge--noise-11';
    case '20': return 'status-badge status-badge--noise-20';
    case '21': return 'status-badge status-badge--noise-21';
    case '99': return 'status-badge status-badge--stopped';
    default:   return 'status-badge status-badge--unknown';
  }
}

/**
 * 気温ステータスバッジの CSS クラス名を返す。
 * statusNo に基づいてオリジナルの配色を適用する。
 */
export function getTemperatureStatusBadgeClass(statusNo: string | undefined): string {
  switch (statusNo) {
    case '1':  return 'status-badge status-badge--temp-1';
    case '2':  return 'status-badge status-badge--temp-2';
    case '3':  return 'status-badge status-badge--temp-3';
    case '4':  return 'status-badge status-badge--temp-4';
    case '5':  return 'status-badge status-badge--temp-5';
    case '6':  return 'status-badge status-badge--temp-6';
    case '7':  return 'status-badge status-badge--temp-7';
    case '8':  return 'status-badge status-badge--temp-8';
    case '9':  return 'status-badge status-badge--temp-9';
    case '10': return 'status-badge status-badge--temp-10';
    case '99': return 'status-badge status-badge--stopped';
    default:   return 'status-badge status-badge--unknown';
  }
}

// ---------------------------------------------------------------------------
// マーカー用カラー — マップピンの背景色（hex）を返す
// ---------------------------------------------------------------------------

export const WBGT_MARKER_COLORS: Record<string, string> = {
  '1':  '#305496',
  '2':  '#548235',
  '3':  '#ffff00',
  '10': '#ff9900',
  '11': '#ff0000',
  '20': '#a50021',
  '99': '#757575',
};

export const TEMP_MARKER_COLORS: Record<string, string> = {
  '1':  '#B40068',
  '2':  '#FF2800',
  '3':  '#FF9900',
  '4':  '#FAF500',
  '5':  '#FFFF96',
  '6':  '#FFFFF0',
  '7':  '#B9EBFF',
  '8':  '#0096FF',
  '9':  '#0041FF',
  '10': '#002080',
  '99': '#757575',
};

export const WIND_MARKER_COLORS: Record<string, string> = {
  '1':  '#003366',
  '2':  '#339933',
  '3':  '#CCFF33',
  '10': '#FFFF00',
  '20': '#FF0000',
  '99': '#757575',
};

export const NOISE_MARKER_COLORS: Record<string, string> = {
  '1':  '#003366',
  '2':  '#339933',
  '10': '#FFFF00',
  '11': '#CCFF33',
  '20': '#FF0000',
  '21': '#FF6600',
  '99': '#757575',
};

export const WIND_SEVERITY: Record<string, number> = {
  '99': 0,
  '1':  1,
  '2':  2,
  '3':  3,
  '10': 4,
  '20': 5,
};

function formatDegrees(value: number): string {
  return Number.isInteger(value) ? String(value) : String(value).replace(/\.0$/, '');
}

function toNumber(value: number | string | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

export function normalizeWbgtThresholds(input: PartialWbgtThresholdInput | undefined): WbgtThresholds | undefined {
  if (!input) return undefined;

  const keys: (keyof WbgtThresholds)[] = ['almostSafe', 'caution', 'warning', 'severeWarning', 'danger'];
  const hasAnyValue = keys.some(key => toNumber(input[key]) !== undefined);
  if (!hasAnyValue) return undefined;

  return {
    almostSafe: toNumber(input.almostSafe) ?? DEFAULT_WBGT_THRESHOLDS.almostSafe,
    caution: toNumber(input.caution) ?? DEFAULT_WBGT_THRESHOLDS.caution,
    warning: toNumber(input.warning) ?? DEFAULT_WBGT_THRESHOLDS.warning,
    severeWarning: toNumber(input.severeWarning) ?? DEFAULT_WBGT_THRESHOLDS.severeWarning,
    danger: toNumber(input.danger) ?? DEFAULT_WBGT_THRESHOLDS.danger,
  };
}

export function validateWbgtThresholdOrder(thresholds: WbgtThresholds | undefined): boolean {
  if (!thresholds) return true;
  return thresholds.almostSafe < thresholds.caution
    && thresholds.caution < thresholds.warning
    && thresholds.warning < thresholds.severeWarning
    && thresholds.severeWarning < thresholds.danger;
}

export function normalizeTemperatureThresholds(input: PartialTemperatureThresholdInput | undefined): TemperatureThresholds | undefined {
  if (!input) return undefined;

  const keys: (keyof TemperatureThresholds)[] = [
    'freezingDanger',
    'freezingCaution',
    'coldCaution',
    'mild',
    'tenOrMore',
    'fifteenOrMore',
    'twentyOrMore',
    'twentyFiveOrMore',
    'thirtyOrMore',
    'thirtyFiveOrMore',
  ];
  const hasAnyValue = keys.some(key => toNumber(input[key]) !== undefined);
  if (!hasAnyValue) return undefined;

  return {
    freezingDanger: toNumber(input.freezingDanger) ?? DEFAULT_TEMPERATURE_THRESHOLDS.freezingDanger,
    freezingCaution: toNumber(input.freezingCaution) ?? DEFAULT_TEMPERATURE_THRESHOLDS.freezingCaution,
    coldCaution: toNumber(input.coldCaution) ?? DEFAULT_TEMPERATURE_THRESHOLDS.coldCaution,
    mild: toNumber(input.mild) ?? DEFAULT_TEMPERATURE_THRESHOLDS.mild,
    tenOrMore: toNumber(input.tenOrMore) ?? DEFAULT_TEMPERATURE_THRESHOLDS.tenOrMore,
    fifteenOrMore: toNumber(input.fifteenOrMore) ?? DEFAULT_TEMPERATURE_THRESHOLDS.fifteenOrMore,
    twentyOrMore: toNumber(input.twentyOrMore) ?? DEFAULT_TEMPERATURE_THRESHOLDS.twentyOrMore,
    twentyFiveOrMore: toNumber(input.twentyFiveOrMore) ?? DEFAULT_TEMPERATURE_THRESHOLDS.twentyFiveOrMore,
    thirtyOrMore: toNumber(input.thirtyOrMore) ?? DEFAULT_TEMPERATURE_THRESHOLDS.thirtyOrMore,
    thirtyFiveOrMore: toNumber(input.thirtyFiveOrMore) ?? DEFAULT_TEMPERATURE_THRESHOLDS.thirtyFiveOrMore,
  };
}

export function validateTemperatureThresholdOrder(thresholds: TemperatureThresholds | undefined): boolean {
  if (!thresholds) return true;
  const cautionSide = thresholds.freezingDanger < thresholds.freezingCaution
    && thresholds.freezingCaution < thresholds.coldCaution;
  const boundary = thresholds.coldCaution <= thresholds.mild;
  const regularSide = thresholds.mild < thresholds.tenOrMore
    && thresholds.tenOrMore < thresholds.fifteenOrMore
    && thresholds.fifteenOrMore < thresholds.twentyOrMore
    && thresholds.twentyOrMore < thresholds.twentyFiveOrMore
    && thresholds.twentyFiveOrMore < thresholds.thirtyOrMore
    && thresholds.thirtyOrMore < thresholds.thirtyFiveOrMore;
  return cautionSide && boundary && regularSide;
}

export function buildWbgtLegendItems(thresholds?: WbgtThresholds | null): StatusLegendItem[] {
  const values = thresholds ?? DEFAULT_WBGT_THRESHOLDS;
  const almostSafe = formatDegrees(values.almostSafe);
  const caution = formatDegrees(values.caution);
  const warning = formatDegrees(values.warning);
  const severeWarning = formatDegrees(values.severeWarning);
  const danger = formatDegrees(values.danger);

  return [
    { statusNo: '20', label: '危険', range: `${danger}℃以上`, color: WBGT_MARKER_COLORS['20'] },
    { statusNo: '11', label: '厳重警戒', range: `${severeWarning}℃〜${danger}℃`, color: WBGT_MARKER_COLORS['11'] },
    { statusNo: '10', label: '警戒', range: `${warning}℃〜${severeWarning}℃`, color: WBGT_MARKER_COLORS['10'] },
    { statusNo: '3', label: '注意', range: `${caution}℃〜${warning}℃`, color: WBGT_MARKER_COLORS['3'] },
    { statusNo: '2', label: 'ほぼ安全', range: `${almostSafe}℃〜${caution}℃`, color: WBGT_MARKER_COLORS['2'] },
    { statusNo: '1', label: 'ほぼ安全', range: `${almostSafe}℃未満`, color: WBGT_MARKER_COLORS['1'] },
    { statusNo: '99', label: '停止', range: '信号なし又はセンサー未設定', color: WBGT_MARKER_COLORS['99'] },
  ];
}

export function buildTemperatureLegendItems(thresholds?: TemperatureThresholds | null): StatusLegendItem[] {
  const values = thresholds ?? DEFAULT_TEMPERATURE_THRESHOLDS;
  const freezingDanger = formatDegrees(values.freezingDanger);
  const freezingCaution = formatDegrees(values.freezingCaution);
  const coldCaution = formatDegrees(values.coldCaution);
  const mild = formatDegrees(values.mild);
  const tenOrMore = formatDegrees(values.tenOrMore);
  const fifteenOrMore = formatDegrees(values.fifteenOrMore);
  const twentyOrMore = formatDegrees(values.twentyOrMore);
  const twentyFiveOrMore = formatDegrees(values.twentyFiveOrMore);
  const thirtyOrMore = formatDegrees(values.thirtyOrMore);
  const thirtyFiveOrMore = formatDegrees(values.thirtyFiveOrMore);

  return [
    { statusNo: '10', label: '要注意', group: '要注意', range: `${freezingDanger}℃以下`, color: TEMP_MARKER_COLORS['10'] },
    { statusNo: '9', label: '要注意', group: '要注意', range: `${freezingCaution}℃以下`, color: TEMP_MARKER_COLORS['9'] },
    { statusNo: '8', label: '要注意', group: '要注意', range: `${coldCaution}℃以下`, color: TEMP_MARKER_COLORS['8'] },
    { statusNo: '7', label: 'その他', group: 'その他', range: `${mild}℃以上`, color: TEMP_MARKER_COLORS['7'] },
    { statusNo: '6', label: 'その他', group: 'その他', range: `${tenOrMore}℃以上`, color: TEMP_MARKER_COLORS['6'] },
    { statusNo: '5', label: 'その他', group: 'その他', range: `${fifteenOrMore}℃以上`, color: TEMP_MARKER_COLORS['5'] },
    { statusNo: '4', label: 'その他', group: 'その他', range: `${twentyOrMore}℃以上`, color: TEMP_MARKER_COLORS['4'] },
    { statusNo: '3', label: 'その他', group: 'その他', range: `${twentyFiveOrMore}℃以上`, color: TEMP_MARKER_COLORS['3'] },
    { statusNo: '2', label: 'その他', group: 'その他', range: `${thirtyOrMore}℃以上`, color: TEMP_MARKER_COLORS['2'] },
    { statusNo: '1', label: 'その他', group: 'その他', range: `${thirtyFiveOrMore}℃以上`, color: TEMP_MARKER_COLORS['1'] },
    { statusNo: '99', label: '停止', group: '停止', range: '信号なし又はセンサー未設定', color: TEMP_MARKER_COLORS['99'] },
  ];
}

export function buildWindLegendItems(): StatusLegendItem[] {
  return [
    { statusNo: '20', label: '警報', range: '10m/s以上', color: WIND_MARKER_COLORS['20'] },
    { statusNo: '10', label: '注意', range: '8m/s以上10m/s未満', color: WIND_MARKER_COLORS['10'] },
    { statusNo: '3', label: '注意', range: '5m/s以上8m/s未満', color: WIND_MARKER_COLORS['3'] },
    { statusNo: '2', label: 'ほぼ安全', range: '2m/s以上5m/s未満', color: WIND_MARKER_COLORS['2'] },
    { statusNo: '1', label: 'ほぼ安全', range: '2m/s未満', color: WIND_MARKER_COLORS['1'] },
    { statusNo: '99', label: '停止', range: '信号なし', color: WIND_MARKER_COLORS['99'] },
  ];
}

/** マーカー用のステータスカラー（hex）を返す */
export function getMarkerColor(statusNo: string | undefined, isWinter: boolean): string {
  if (!statusNo) return '#9ca3af'; // default gray
  const map = isWinter ? TEMP_MARKER_COLORS : WBGT_MARKER_COLORS;
  return map[statusNo] ?? '#9ca3af';
}

/** 風速マーカー用のステータスカラー（hex）を返す */
export function getWindMarkerColor(statusNo: string | undefined): string {
  if (!statusNo) return '#9ca3af';
  return WIND_MARKER_COLORS[statusNo] ?? '#9ca3af';
}

/** マーカー用の zIndex を返す（危険度が高いほど大きい値 = 上に表示） */
export function getMarkerZIndex(statusNo: string | undefined, isWinter: boolean): number {
  if (!statusNo) return 0;
  const severityMap = isWinter ? TEMP_SEVERITY : WBGT_SEVERITY;
  return (severityMap[statusNo] ?? 0) * 100;
}

/** 風速マーカー用の zIndex を返す（危険度が高いほど大きい値 = 上に表示） */
export function getWindMarkerZIndex(statusNo: string | undefined): number {
  if (!statusNo) return 0;
  return (WIND_SEVERITY[statusNo] ?? 0) * 100;
}

/** 季節に応じたステータスバッジクラスを返す（WBGT or 気温） */
export function getSeasonalStatusBadgeClass(statusNo: string | undefined, isWinter: boolean): string {
  return isWinter
    ? getTemperatureStatusBadgeClass(statusNo)
    : getWbgtStatusBadgeClass(statusNo);
}
